import { TFunction } from 'i18next'

import {
    FALLBACK_DATE_FORMAT,
    FALLBACK_DATE_TIME_FORMAT,
    FALLBACK_TIME_FORMAT,
    getDateFormat,
    getDateTimeFormat,
    getTimeFormat,
} from './appLocale'
import { DateTime, dateTime, mapOptionalDateTime } from './Time'

export const formatCalendarTime =
    (translate: TFunction) =>
    (t: DateTime): string => {
        const time = getTimeFormat()
        const dateTimeFmt = getDateTimeFormat()
        return t.calendar(undefined, {
            sameDay: `[${translate('time.today', { ns: 'common' })}], ${time}`,
            nextDay: `[${translate('time.tomorrow', { ns: 'common' })}], ${time}`,
            nextWeek: dateTimeFmt,
            lastDay: `[${translate('time.yesterday', { ns: 'common' })}], ${time}`,
            lastWeek: dateTimeFmt,
            sameElse: dateTimeFmt,
        })
    }

export const formatCalendarDay =
    (translate: TFunction) =>
    (t: DateTime): string => {
        const dateFmt = getDateFormat()
        return t.calendar(undefined, {
            sameDay: `[${translate('time.today', { ns: 'common' })}]`,
            nextDay: `[${translate('time.tomorrow', { ns: 'common' })}]`,
            nextWeek: dateFmt,
            lastDay: `[${translate('time.yesterday', { ns: 'common' })}]`,
            lastWeek: dateFmt,
            sameElse: dateFmt,
        })
    }

export const formatOptionalCalendarTime =
    (translate: TFunction) =>
    (t?: DateTime): string | undefined =>
        mapOptionalDateTime(x => formatCalendarTime(translate)(x))(t)

export const formatDateTime = (t: DateTime, format?: string): string =>
    t.format(format || getDateTimeFormat())

export const formatTime = (t: DateTime, format?: string): string =>
    t.format(format || getTimeFormat())

export const formatHour = (t: DateTime): string => t.format('HH')

export const formatDay = (t: DateTime): string => t.format('DD')
export const formatDayLong = (t: DateTime): string => `${t.format('ddd')}.`
export const formatDayMonth = (t: DateTime): string => t.format('DD.MM')

export const formatDate = (t: DateTime, format?: string): string =>
    t.format(format || getDateFormat())

export const formatOptionalDateTime = (
    value?: DateTime | string,
    format?: string,
): string | undefined =>
    mapOptionalDateTime(t => formatDateTime(t, format))(
        typeof value === 'string' ? dateTime(value) : value,
    )

export const formatOptionalTime = (value?: DateTime, format?: string): string | undefined =>
    mapOptionalDateTime(t => formatTime(t, format))(value)

/**
 * @deprecated Use `getDateFormat()` from `./appLocale` so the value follows
 * the tenant config. Kept as a const for back-compat with existing imports.
 */
export const defaultDateFormat = FALLBACK_DATE_FORMAT
/**
 * @deprecated Use `getDateTimeFormat()` from `./appLocale`.
 */
export const defaultDateTimeFormat = FALLBACK_DATE_TIME_FORMAT
/**
 * @deprecated Use `getTimeFormat()` from `./appLocale`.
 */
export const defaultTimeFormat = FALLBACK_TIME_FORMAT

export const fromDateTimeFormat = (t: TFunction) =>
    `[${t('datetime.from', { ns: 'common' })}] ${getDateTimeFormat()}`
export const toDateTimeFormat = (t: TFunction) =>
    `[${t('datetime.to', { ns: 'common' })}] ${getDateTimeFormat()}`

export const fromDateFormat = (t: TFunction) =>
    `[${t('datetime.from', { ns: 'common' })}] ${getDateFormat()}`
export const toDateFormat = (t: TFunction) =>
    `[${t('datetime.to', { ns: 'common' })}] ${getDateFormat()}`

export const formatFromDateTime =
    (translate: TFunction) =>
    (t: DateTime): string =>
        formatDateTime(t, fromDateTimeFormat(translate))

export const formatToDateTime =
    (translate: TFunction) =>
    (t: DateTime): string =>
        formatDateTime(t, toDateTimeFormat(translate))

export const getMonthName = (m: number): string => dateTime(m, 'M').format('MMMM')
